import { FC, ReactNode, useRef, useState, useEffect, ChangeEvent } from 'react'
import {
  useForm,
  FieldValues,
  UseFormRegister,
  FieldError,
  FieldErrors,
  UseFormTrigger,
  UseFormGetFieldState,
  UseFormGetValues,
  SubmitErrorHandler,
  FormState,
} from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import getConfig from 'next/config'
import { EventReportManager } from '../../event-report/event-report-manager'
import { buildCanonicalFormNameByWindowHref } from '../../common/uri-helper'

type ValidateRule = {
  required?: boolean
  min?: number
  max?: number
  minLength?: number
  maxLength?: number
  pattern?: string // is regex
  validate?: (value: string) => boolean
}

export interface BaseProps {
  label: string
  field: string
  placeholder?: string
  register: UseFormRegister<FieldValues>
  rule?: ValidateRule
  errors?: FieldErrors
  className?: string
  fieldClassName?: string
  internalSlot?: ReactNode
}

export interface RadioGroupProps extends BaseProps {
  options: { label: string; value: string | number }[]
}

const RadioGroup: FC<RadioGroupProps> = ({
  label,
  field,
  placeholder,
  register,
  rule,
  errors,
  className,
  fieldClassName,
  internalSlot,
  options,
}) => {
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (errors && errors[field]) {
      // @ts-ignore
      setErrorMessage(errors[field] as string)
    }
  }, [errors, field])

  const { publicRuntimeConfig } = getConfig()
  const type = 'radio'

  const { onChange, onBlur, name, ref } = register(field)

  function handleChange(evt: ChangeEvent<HTMLInputElement>) {
    const target = evt.target as HTMLInputElement
    const validity = target.validity
    const validationMessage = target.validationMessage

    if (validationMessage && !validity.valid) {
      setErrorMessage(validationMessage)
    } else {
      setErrorMessage('')
    }
  }

  return (
    <div
      className={[className, 'relative flex flex-col flex-1 text-base text-black/90 col-span-2 laptop:col-span-1'].join(
        ' '
      )}
    >
      <label className="font-semibold">
        {label}
        {rule && rule.required && <sup className="text-[#f52242]">*</sup>}
      </label>
      {internalSlot && internalSlot}

      <div className="flex flex-row flex-wrap gap-12 mt-3 laptop:mt-2">
        {options &&
          options.map((opt) => {
            return (
              <div key={opt.value} className="flex flex-row items-center gap-2">
                <input
                  className={[fieldClassName, 'w-4 h-4'].join(' ')}
                  id={opt.label + '-' + opt.value}
                  type={type}
                  value={opt.value}
                  {...rule}
                  onChange={(evt) => {
                    onChange(evt)
                    handleChange(evt)
                  }}
                  onBlur={onBlur}
                  name={name}
                  ref={ref}
                />
                <label className="text-16 text-black/60 font-normal" htmlFor={opt.label + '-' + opt.value}>
                  {opt.label}
                </label>
              </div>
            )
          })}
      </div>

      {errorMessage && (
        <p role="alert" className="absolute left-0 bottom-0 translate-y-full text-red-500 text-xs">
          {errorMessage}
        </p>
      )}
    </div>
  )
}

export interface InputProps extends BaseProps {}

const Input: FC<InputProps> = ({
  label,
  field,
  placeholder,
  register,
  rule,
  errors,
  className,
  fieldClassName,
  internalSlot,
}) => {
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (errors && errors[field]) {
      // @ts-ignore
      setErrorMessage(errors[field] as string)
    }
  }, [errors, field])

  const { publicRuntimeConfig } = getConfig()
  let type = 'text'
  const lowerCaseField = field.toLowerCase()
  if (lowerCaseField.includes('email')) {
    type = 'email'
  }

  if (lowerCaseField.includes('phonenumber') || lowerCaseField.includes('mobile')) {
    type = 'tel'
  }

  if (lowerCaseField.includes('date')) {
    type = 'date'
  }

  function handleEmailChange(evt: ChangeEvent<HTMLInputElement>) {
    // prevent from email "temporary-mail.net"
    const target = evt.target as HTMLInputElement
    const emailValue = target.value
    const validity = target.validity
    const invalidEmailDomainIncluded = publicRuntimeConfig.invalidEmailDomains?.some((domain: string) =>
      emailValue.includes(domain)
    )

    if (invalidEmailDomainIncluded) {
      target.setCustomValidity('Please fill in the correct email address')
    } else if (validity.customError) {
      target.setCustomValidity('')
    }
  }

  function handleChange(evt: ChangeEvent<HTMLInputElement>) {
    const target = evt.target as HTMLInputElement
    const validity = target.validity
    if (type === 'email') {
      handleEmailChange(evt)
    }

    const validationMessage = target.validationMessage
    if (validationMessage && !validity.valid) {
      setErrorMessage(validationMessage)
    } else {
      setErrorMessage('')
    }
  }

  const { onChange, onBlur, name, ref } = register(field)

  return (
    <div
      className={[className, 'relative flex flex-col flex-1 text-base text-black/90 col-span-2 laptop:col-span-1'].join(
        ' '
      )}
    >
      <label className="font-semibold">
        {label}
        {rule && rule.required && <sup className="text-[#f52242]">*</sup>}
      </label>
      {internalSlot && internalSlot}
      <input
        className={[
          fieldClassName,
          'mt-3 bg-[#f2f3f5] laptop:mt-2 h-10 laptop:h-13 px-3 laptop:px-4 laptop:text-base text-14 border-none outline-none placeholder:text-black/40',
        ].join(' ')}
        onBlur={onBlur}
        name={name}
        ref={ref}
        placeholder={placeholder}
        maxLength={rule?.maxLength}
        {...rule}
        type={type}
        onChange={(evt) => {
          onChange(evt)
          handleChange(evt)
        }}
      />
      {errorMessage && (
        <p role="alert" className="absolute left-0 bottom-0 translate-y-full text-red-500 text-xs">
          {errorMessage}
        </p>
      )}
    </div>
  )
}

export interface TextProps extends BaseProps {}
const Text: FC<TextProps> = ({
  label,
  field,
  placeholder,
  register,
  rule,
  errors,
  className,
  fieldClassName,
  internalSlot,
}) => {
  return (
    <div className={[className, 'flex flex-col flex-1 text-base text-black/90 col-span-2'].join(' ')}>
      <label className="font-semibold">
        {label}
        {rule && rule.required && <sup className="text-[#f52242]">*</sup>}
      </label>
      {internalSlot && internalSlot}
      <textarea
        className={[
          fieldClassName,
          'mt-3 laptop:mt-2 bg-[#f2f3f5] border-none resize-none px-3 laptop:px-4 laptop:text-base text-14 laptop:h-110px h-40 overflow-auto placeholder:text-black/40',
        ].join(' ')}
        {...register(field)}
        placeholder={placeholder}
        maxLength={rule?.maxLength}
        {...rule}
      />
    </div>
  )
}

type SelectOption = { label: string; value: string | number }
interface SelectProps extends BaseProps {
  options: SelectOption[]
}

const Select: FC<SelectProps> = ({
  label,
  field,
  placeholder,
  register,
  options,
  rule,
  errors,
  className,
  fieldClassName,
  internalSlot,
}) => {
  const [curLabel, setCurLabel] = useState<string | null>(placeholder ?? null)

  const { onChange, onBlur, name, ref } = register(field)

  return (
    <div className={[className, 'flex flex-col flex-1 text-base text-black/90 col-span-2'].join(' ')}>
      <label className="font-semibold">
        {label}
        {rule && rule.required && <sup className="text-[#f52242]">*</sup>}
      </label>
      {internalSlot && internalSlot}
      <select
        className={[
          curLabel === placeholder ? 'text-black/40' : 'text-black/90',
          fieldClassName,
          'mt-3 laptop:mt-2 bg-[#f2f3f5] h-10 laptop:h-13 laptop:text-base text-14 border-none outline-none px-3 laptop:px-4',
        ].join(' ')}
        {...rule}
        onBlur={onBlur}
        name={name}
        ref={ref}
        onChange={(e) => {
          onChange(e)
          setCurLabel(e?.target?.textContent)
        }}
      >
        {(placeholder ? [{ label: placeholder, value: '' }, ...options] : options).map((option) => (
          <option key={option?.value} value={option?.value} disabled={option.label === placeholder}>
            {option?.label}
          </option>
        ))}
      </select>
    </div>
  )
}

export type FieldItem = {
  type: 'INPUT' | 'SELECT' | 'TEXT' | 'RADIO_GROUP'
  label: string
  fieldName: string
  placeholder: string
  options?: SelectOption[]
  extra?: string
  rule?: ValidateRule
  className?: string
  fieldClassName?: string
  internalSlot?: ReactNode
}

export interface PdFormProps {
  defaultFormValues?: FieldValues
  fields: FieldItem[]
  schema?: yup.AnyObjectSchema
  bottomRender?: (
    trigger: UseFormTrigger<FieldValues>,
    getValues: UseFormGetValues<FieldValues>,
    formState: FormState<FieldValues>,
    formValidity: boolean
  ) => ReactNode
  onSubmit?: (data: FieldValues) => void
  onInvalid?: SubmitErrorHandler<FieldValues>
}

const PdForm: FC<PdFormProps> = ({ defaultFormValues, fields, schema, bottomRender, onSubmit, onInvalid }) => {
  const { register, handleSubmit, trigger, getValues, formState, control } = useForm({
    defaultValues: defaultFormValues,
    resolver: yupResolver(schema ?? yup.object()),
  })
  const [formValidity, setFormValidity] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const errors = formState.errors
  const formStartEventRef = useRef(false)

  const onValid = (data: FieldValues) => {
    console.log('getValues: ', JSON.stringify(getValues()))
    // submit button clicked, report event here.
    const event = { data: { event: 'submit_lead_form' } }
    EventReportManager.getInstance().fireEventImmediately(event, true)
    onSubmit?.(data)
  }

  const onChange = () => {
    const checkValidity = formRef.current?.checkValidity()
    console.log('onChange.checkValidity: ', checkValidity)
    setFormValidity(!!checkValidity)

    // fire form_start event
    if (!formStartEventRef.current) {
      const evt = {
        data: {
          event: 'lead_form_start',
          form_name: buildCanonicalFormNameByWindowHref(),
          page_url: window.location.href,
        },
      }
      EventReportManager.getInstance().fireEventImmediately(evt, true)
      console.log('Fire form_start event', evt)
      formStartEventRef.current = true
    }
  }

  return (
    <div className="relative w-full">
      <form
        ref={formRef}
        onChange={() => onChange()}
        onSubmit={handleSubmit(onValid, onInvalid)}
        className="grid grid-cols-2 flex-wrap laptop:gap-x-4 laptop:gap-y-12 gap-y-8"
      >
        {fields.map((field) => {
          if (field.type === 'RADIO_GROUP') {
            return (
              <RadioGroup
                className={field.className}
                fieldClassName={field.fieldClassName}
                key={field.label}
                label={field.label}
                field={field.fieldName}
                placeholder={field.placeholder}
                register={register}
                rule={field.rule}
                errors={errors}
                internalSlot={field.internalSlot}
                options={field.options ?? []}
              />
            )
          } else if (field.type === 'INPUT') {
            return (
              <Input
                className={field.className}
                fieldClassName={field.fieldClassName}
                key={field.label}
                label={field.label}
                field={field.fieldName}
                placeholder={field.placeholder}
                register={register}
                rule={field.rule}
                errors={errors}
                internalSlot={field.internalSlot}
              />
            )
          } else if (field.type === 'SELECT') {
            return (
              <Select
                className={field.className}
                fieldClassName={field.fieldClassName}
                key={field.label}
                options={field.options ?? []}
                label={field.label}
                field={field.fieldName}
                placeholder={field.placeholder}
                register={register}
                rule={field.rule}
                errors={errors}
                internalSlot={field.internalSlot}
              />
            )
          } else if (field.type === 'TEXT') {
            return (
              <Text
                className={field.className}
                fieldClassName={field.fieldClassName}
                key={field.label}
                label={field.label}
                field={field.fieldName}
                placeholder={field.placeholder}
                register={register}
                rule={field.rule}
                errors={errors}
                internalSlot={field.internalSlot}
              />
            )
          }
          return null
        })}
        <div className="col-span-2">{bottomRender && bottomRender(trigger, getValues, formState, formValidity)}</div>

        {/* 蜜罐 */}
        <div className="hidden">
          <input type="text" name="PHONE_NUMBER" />
          <input type="text" name="E_MAIL" />
        </div>
      </form>
    </div>
  )
}

export default PdForm
